import { defineStore } from "pinia";
import notificationService from "@/services/notification.service";
import { toast } from "@/utils/toast";

export const useNotificationStore = defineStore("notifications", {
  state: () => ({
    items: [],
    recent: [],
    meta: { total: 0, per_page: 20, current_page: 1, last_page: 1 },
    unreadCount: 0,
    loading: false,
    dropdownLoading: false,
    actionLoading: false,
    error: null,
    filters: {
      status: "all",
      category: "",
      q: "",
      per_page: 20,
      page: 1,
    },
  }),

  getters: {
    unreadItems(state) {
      return state.items.filter((item) => !item.is_read);
    },
  },

  actions: {
    async fetchNotifications(extra = {}) {
      this.loading = true;
      this.error = null;

      try {
        const params = { ...this.filters, ...extra };
        const response = await notificationService.list(params);
        this.items = Array.isArray(response.data?.data) ? response.data.data : [];
        this.meta = response.data?.meta || this.meta;
        this.unreadCount = Number(response.data?.meta?.unread_count ?? this.unreadCount ?? 0);
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch notifications";
        toast(this.error, "error");
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchRecent(limit = 6) {
      this.dropdownLoading = true;
      try {
        const response = await notificationService.list({
          per_page: limit,
          page: 1,
        });
        this.recent = Array.isArray(response.data?.data) ? response.data.data : [];
        this.unreadCount = Number(response.data?.meta?.unread_count ?? this.unreadCount ?? 0);
        return this.recent;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch recent notifications";
        throw error;
      } finally {
        this.dropdownLoading = false;
      }
    },

    async refreshUnreadCount() {
      try {
        const response = await notificationService.unreadCount();
        this.unreadCount = Number(response.data?.unread_count ?? 0);
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to load unread notifications";
      }
    },

    async markRead(id, silent = false) {
      this.actionLoading = true;
      try {
        const response = await notificationService.markRead(id);
        this.unreadCount = Number(response.data?.unread_count ?? this.unreadCount ?? 0);
        this.items = this.items.map((item) =>
          item.id === id ? { ...item, is_read: true, read_at: response.data?.notification?.read_at || item.read_at } : item,
        );
        this.recent = this.recent.map((item) =>
          item.id === id ? { ...item, is_read: true, read_at: response.data?.notification?.read_at || item.read_at } : item,
        );
        if (!silent) toast("Notification marked as read");
      } catch (error) {
        const message = error.response?.data?.message || "Failed to mark notification as read";
        if (!silent) toast(message, "error");
        throw error;
      } finally {
        this.actionLoading = false;
      }
    },

    async markAllRead(silent = false) {
      this.actionLoading = true;
      try {
        await notificationService.markAllRead();
        this.unreadCount = 0;
        this.items = this.items.map((item) => ({ ...item, is_read: true, read_at: item.read_at || new Date().toISOString() }));
        this.recent = this.recent.map((item) => ({ ...item, is_read: true, read_at: item.read_at || new Date().toISOString() }));
        if (!silent) toast("All notifications marked as read");
      } catch (error) {
        const message = error.response?.data?.message || "Failed to mark all notifications as read";
        if (!silent) toast(message, "error");
        throw error;
      } finally {
        this.actionLoading = false;
      }
    },

    async deleteNotification(id, silent = false) {
      this.actionLoading = true;
      try {
        await notificationService.remove(id);
        this.items = this.items.filter((item) => item.id !== id);
        this.recent = this.recent.filter((item) => item.id !== id);
        if (this.meta.total > 0) {
          this.meta = { ...this.meta, total: this.meta.total - 1 };
        }
        await this.refreshUnreadCount();
        if (!silent) toast("Notification deleted");
      } catch (error) {
        const message = error.response?.data?.message || "Failed to delete notification";
        if (!silent) toast(message, "error");
        throw error;
      } finally {
        this.actionLoading = false;
      }
    },

    async clearAll() {
      this.actionLoading = true;
      try {
        await notificationService.clearAll();
        this.items = [];
        this.recent = [];
        this.unreadCount = 0;
        this.meta = { ...this.meta, total: 0, current_page: 1, last_page: 1 };
        toast("Notifications cleared");
      } catch (error) {
        const message = error.response?.data?.message || "Failed to clear notifications";
        toast(message, "error");
        throw error;
      } finally {
        this.actionLoading = false;
      }
    },
  },
});
